"use client";
import React, { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { FaGithub, FaInstagramSquare, FaLinkedin } from "react-icons/fa";
import { Element } from "react-scroll";
import { fadeUp, ROLES } from "@/constants/banner-constants";
import { SplitText } from "./banner/SplitText";
import { MagneticLink } from "./banner/MagneticLink";
import { GridLines } from "./banner/GridLines";
import { Particles } from "./banner/Particles";

// Design concept: Dark Cyberpunk Editorial
// Deep #05050b void background with electric cyan #00f0ff + electric blue #0047ff as the only colors — no fuchsia/pink anywhere.

const Banner = () => {
  const sectionRef = useRef(null);

  /* Mouse parallax for orbs */
  const rawX = useMotionValue(0.5);
  const rawY = useMotionValue(0.5);
  const orbX = useSpring(useTransform(rawX, [0, 1], [-40, 40]), {
    stiffness: 50,
    damping: 18,
  });
  const orbY = useSpring(useTransform(rawY, [0, 1], [-25, 25]), {
    stiffness: 50,
    damping: 18,
  });

  const onMouseMove = (e) => {
    const r = sectionRef.current?.getBoundingClientRect();
    if (!r) return;
    rawX.set((e.clientX - r.left) / r.width);
    rawY.set((e.clientY - r.top) / r.height);
  };

  return (
    <Element name="home">
      <section
        ref={sectionRef}
        onMouseMove={onMouseMove}
        className="relative h-screen flex items-center justify-center overflow-hidden mt-0 lg:mt-20 mx-auto"
        style={{ background: "#05050b" }}
      >
        {/* Grid */}
        <GridLines />

        {/* Particles */}
        <Particles />

        {/* ── Gradient orbs (mouse-parallax) ── */}
        <motion.div
          style={{ x: orbX, y: orbY }}
          className="absolute inset-0 pointer-events-none"
          aria-hidden
        >
          <div
            className="absolute -top-[15%] -left-[10%] w-[65vw] h-[65vw] max-w-[750px] max-h-[750px] rounded-full"
            style={{
              background:
                "radial-gradient(circle, rgba(0,71,255,0.28) 0%, rgba(102,0,255,0.18) 45%, transparent 72%)",
              filter: "blur(80px)",
            }}
          />
          <div
            className="absolute -bottom-[20%] -right-[8%] w-[60vw] h-[60vw] max-w-[700px] max-h-[700px] rounded-full"
            style={{
              background:
                "radial-gradient(circle, rgba(0,240,255,0.22) 0%, rgba(0,71,255,0.15) 50%, transparent 75%)",
              filter: "blur(90px)",
            }}
          />
          <div
            className="absolute top-[40%] left-[45%] w-[25vw] h-[25vw] max-w-[350px] max-h-[350px] rounded-full"
            style={{
              background:
                "radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 70%)",
              filter: "blur(50px)",
            }}
          />
        </motion.div>

        {/* ── Top border beam ── */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 1.4, delay: 1.6, ease: [0.22, 1, 0.36, 1] }}
          className="absolute top-0 left-0 right-0 h-[1.5px] origin-left pointer-events-none"
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, #0047ff 30%, #00f0ff 70%, transparent 100%)",
          }}
        />

        {/* ── Side index label ── */}
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.8, duration: 0.7 }}
          className="absolute left-6 top-1/2 -translate-y-1/2 hidden xl:flex flex-col items-center gap-3"
        >
          <span
            className="text-[9px] tracking-[0.35em] text-white/20 uppercase"
            style={{
              writingMode: "vertical-lr",
              fontFamily: "'DM Mono', monospace",
            }}
          >
            {new Date().getFullYear()} — Portfolio
          </span>
          <div className="w-px h-16 bg-gradient-to-b from-transparent via-white/15 to-transparent" />
        </motion.div>

        {/* ── MAIN CONTENT ── */}
        <div className="relative z-10 flex flex-col items-center text-center px-0 w-full max-w-7xl">
          {/* Status pill */}
          <motion.div
            custom={0}
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="mb-8 inline-flex items-center gap-2.5 px-4 py-2 rounded-full
              border border-[#00f0ff]/15 bg-[#00f0ff]/[0.04] backdrop-blur-sm"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute h-full w-full rounded-full bg-[#00f0ff] opacity-70" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#00f0ff]" />
            </span>
            <span
              className="text-[10px] md:text-xs text-[#00f0ff]/75 tracking-[0.25em] uppercase font-medium"
              style={{ fontFamily: "'DM Mono', monospace" }}
            >
              Available for work
            </span>
          </motion.div>

          {/* NAME — giant split chars */}
          <div style={{ perspective: "900px", overflow: "hidden" }}>
            <h1
              className="text-[clamp(4rem,13vw,10rem)] font-black leading-[0.85] tracking-tight text-white"
              style={{ fontFamily: "'Syne', 'Unbounded', sans-serif" }}
            >
              <div className="overflow-hidden text-left">
                <SplitText text="ABU" className="block" />
                <SplitText text="BOKOR" className="block" />
              </div>
            </h1>
          </div>

          {/* Ghost / outlined subtitle */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 0.95,
              duration: 0.8,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="overflow-hidden"
          >
            <h2
              className="lg:text-[clamp(4rem,10vw,8rem)]       
    text-[clamp(2.5rem,7vw,7rem)]
      md:text-[clamp(4rem,10vw,8rem)] font-black leading-[0.85] tracking-tight mb-4"
              style={{
                fontFamily: "'Syne', 'Unbounded', sans-serif",
                WebkitTextStroke: "1.2px rgba(0,240,255,0.3)",
                color: "transparent",
              }}
            >
              DEVELOPER
            </h2>
          </motion.div>

          {/* Gradient rule */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-lg h-px mb-5 origin-left"
            style={{
              background:
                "linear-gradient(90deg, #0047ff, #00f0ff50, transparent)",
            }}
          />

          {/* Role ticker */}
          {/* <motion.div
            custom={1.1}
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="w-full mx-auto flex items-center justify-center"
          >
            <RoleTicker />
          </motion.div> */}

          {/* Bio line */}
          <motion.p
            custom={1.25}
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="max-w-md text-sm md:text-base text-white/35 leading-relaxed mb-10 font-light"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            Backend-focused Full Stack Engineer with 2+ years experience.
            Shipped 2 apps to production.
          </motion.p>

          {/* CTA row */}
          <motion.div
            custom={1.4}
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="flex flex-wrap items-center justify-center gap-4"
          >
            {/* Primary */}
            <motion.a
              href="#contact"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="relative group inline-flex items-center gap-3
                px-8 py-3.5 rounded-full font-semibold text-sm tracking-[0.18em] uppercase
                text-[#05050b] overflow-hidden cursor-pointer"
              style={{
                background: "linear-gradient(135deg, #00f0ff 0%, #0047ff 100%)",
                boxShadow: "0 0 35px rgba(0,240,255,0.25)",
                fontFamily: "'DM Mono', monospace",
              }}
            >
              <span className="relative z-10">Hire Me</span>
              <motion.span
                className="relative z-10"
                animate={{ x: [0, 5, 0] }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                →
              </motion.span>
              <span
                className="absolute inset-0 z-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(255,255,255,0.2), transparent 60%)",
                }}
              />
            </motion.a>

            {/* Secondary */}
            <motion.a
              href="#projects"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-3
                px-8 py-3.5 rounded-full font-semibold text-sm tracking-[0.18em] uppercase
                text-white/60 border border-white/[0.09]
                hover:border-[#00f0ff]/35 hover:text-[#00f0ff] hover:bg-[#00f0ff]/[0.06]
                transition-all duration-300 backdrop-blur-sm cursor-pointer"
              style={{ fontFamily: "'DM Mono', monospace" }}
            >
              View Work
              <span className="text-base">↗</span>
            </motion.a>

            {/* Divider */}
            <div className="hidden sm:block w-px h-10 bg-white/10" />

            {/* Socials */}
            <div className="flex items-center gap-3">
              {[
                {
                  href: "https://github.com/AbuBokorprog",
                  Icon: FaGithub,
                  label: "GitHub",
                },
                {
                  href: "https://www.linkedin.com/in/md-abu-bokor/",
                  Icon: FaLinkedin,
                  label: "LinkedIn",
                },
                { href: "#", Icon: FaInstagramSquare, label: "Instagram" },
              ].map(({ href, Icon, label }) => (
                <MagneticLink key={label} href={href} label={label}>
                  <Icon className="w-5 h-5 text-white/45 group-hover:text-[#00f0ff] transition-colors duration-300" />
                </MagneticLink>
              ))}
            </div>
          </motion.div>
        </div>

        {/* ── Scroll indicator ── */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.1, duration: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        >
          <span
            className="text-[9px] tracking-[0.32em] uppercase text-white/20"
            style={{ fontFamily: "'DM Mono', monospace" }}
          >
            scroll
          </span>
          <motion.div
            className="w-px h-9 bg-gradient-to-b from-[#00f0ff] to-transparent"
            animate={{ scaleY: [0, 1, 0], opacity: [0, 1, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            style={{ transformOrigin: "top" }}
          />
        </motion.div>

        {/* ── Corner stamp ── */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 0.8 }}
          className="absolute bottom-8 left-7 hidden md:block"
        >
          <span
            // className="text-[9px] tracking-[0.22em] text-white/18"
            className="text-[10px] tracking-[0.32em] text-[#00f0ff]/60 uppercase"
            style={{ fontFamily: "'DM Mono', monospace" }}
          >
            01 / INTRO
          </span>
        </motion.div>
      </section>

      {/* Google Fonts import */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800;900&family=DM+Mono:ital,wght@0,300;0,400;0,500;1,300&family=DM+Sans:wght@300;400;500&display=swap');
      `}</style>
    </Element>
  );
};

export default Banner;
